/**
 * ISWC (ISO 15707:2022): una T, 9 dígitos de obra y 1 dígito verificador.
 *
 * Se guarda la forma de 11 caracteres (T0345246801), la que usan la ISWC DB de
 * CISAC, CWR y DDEX. En pantalla va con la máscara del portal de CISAC:
 * T-034.524.680-1 (formatIswcForDisplay). Los puntos y guiones "no tienen
 * significado y no se guardan" (ISO/TC46/SC9 N268).
 *
 * El verificador deja pasar el 9% de los errores de un dígito: por eso, cuando
 * no cierra, el mensaje no muestra el dígito "correcto". Sugerir uno induciría
 * a corregir mal.
 *
 * ESPEJO byte-idéntico: jupiter-server/src/common/validation/identifiers/iswc.ts
 * y jupiter-client/src/entities/common/validation/identifiers/iswc.ts
 */

import type { IdentifierCheck } from "./index";

/** Puntos, espacios (con NBSP), guiones Unicode y caracteres de ancho cero. */
const SEPARATORS =
  /[.\s\u200B-\u200D\u2060\uFEFF\u2010-\u2015\u2212\uFE58\uFE63\uFF0D-]/g;

const ISWC_PATTERN = /^T[0-9]{10}$/;

const ZERO_WORK = "000000000";

function upperAsciiLetters(text: string): string {
  return text.replace(/[a-z]/g, (c) => c.toUpperCase());
}

/** S = 1 + Σ i·dᵢ (i = 1..9; la T vale 1) y C = (10 − S mod 10) mod 10. */
function checkDigitOf(nineDigits: string): number {
  let sum = 1;
  for (let i = 0; i < 9; i++) sum += (i + 1) * (nineDigits.charCodeAt(i) - 48);
  return (10 - (sum % 10)) % 10;
}

function isValidCanonical(value: string): boolean {
  return (
    ISWC_PATTERN.test(value) &&
    value.slice(1, 10) !== ZERO_WORK &&
    checkDigitOf(value.slice(1, 10)) === Number(value[10])
  );
}

/** T0345246801 → T-034.524.680-1. Lo que no es un ISWC compacto vuelve igual. */
export function formatIswcForDisplay(canonical: string): string {
  if (!ISWC_PATTERN.test(canonical)) return canonical;
  return `T-${canonical.slice(1, 4)}.${canonical.slice(4, 7)}.${canonical.slice(7, 10)}-${canonical.slice(10)}`;
}

function suggestion(candidate: string): string {
  return isValidCanonical(candidate)
    ? ` ¿Quisiste decir ${formatIswcForDisplay(candidate)}?`
    : "";
}

export function checkIswc(raw: string | null | undefined): IdentifierCheck {
  if (raw === null || raw === undefined) return { value: null };
  const trimmed = String(raw).normalize("NFKC").trim();
  if (trimmed === "") return { value: null };

  const value = upperAsciiLetters(trimmed)
    .replace(/^ISWC:?\s*/, "")
    .replace(SEPARATORS, "");

  if (/^[0-9]{10}$/.test(value)) {
    // No se agrega la T sola: un número cualquiera de 10 dígitos cierra el
    // verificador 1 de cada 10 veces.
    return {
      value,
      error: {
        code: "ISWC_MISSING_T",
        message: `Falta la T inicial: el ISWC es una T seguida de 10 dígitos.${suggestion(`T${value}`)}`,
      },
    };
  }
  if (/^[A-Z][0-9]{10}$/.test(value) && value[0] !== "T") {
    return {
      value,
      error: {
        code: "ISWC_WRONG_PREFIX",
        message: `El ISWC empieza con la letra T, no con la ${value[0]}.`,
      },
    };
  }
  if (/^T[0-9]{9}$/.test(value)) {
    return {
      value,
      error: {
        code: "ISWC_MISSING_CHECK_DIGIT",
        message: "Falta el dígito verificador: el ISWC es una T seguida de 10 dígitos.",
      },
    };
  }
  if (/^T[0-9OIL]{10}$/.test(value) && /[OIL]/.test(value)) {
    const digits = value.slice(1).replace(/O/g, "0").replace(/[IL]/g, "1");
    return {
      value,
      error: {
        code: "ISWC_LETTER_IN_DIGITS",
        message: `El ISWC solo lleva números después de la T.${suggestion(`T${digits}`)}`,
      },
    };
  }
  if (!ISWC_PATTERN.test(value)) {
    // Si no queda nada al normalizar, el valor es lo que se escribió: nunca "" con error.
    return {
      value: value || trimmed,
      error: {
        code: "ISWC_FORMAT",
        message: "El ISWC es una T seguida de 10 dígitos (por ejemplo T-034.524.680-1).",
      },
    };
  }
  if (value.slice(1, 10) === ZERO_WORK) {
    return {
      value,
      error: {
        code: "ISWC_ZERO_WORK",
        message: "El número de obra no puede ser 000000000.",
      },
    };
  }
  if (!isValidCanonical(value)) {
    return {
      value,
      error: {
        code: "ISWC_CHECK_DIGIT",
        message:
          "El dígito verificador no coincide. Revisá los números contra la fuente original.",
      },
    };
  }
  return { value };
}
